import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CheckoutProcessingPage() {
  const { style } = useStyle();

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";
  const panelBg2 = style.darkerPanel || "rgba(6, 9, 14, 0.55)";

  const infoItems = [
    {
      icon: <AutorenewRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "Payment is processing",
      text: "Some payments take a little longer to confirm than others.",
    },
    {
      icon: <MarkEmailReadRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "We’ll update your account",
      text: "As soon as we confirm the payment we will update your account.",
    },
    {
      icon: <DownloadRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "Check My Gear shortly",
      text: "Your items will appear in your My Gear page once everything finishes processing",
    },
  ];

  return (
    <>
      <Seo
        title="Processing Payment | Parish Audio"
        description="Your Parish Audio payment is processing."
        canonical="https://parishaudio.com/checkout-processing"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "clip",
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 980 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              pt: 2,
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                mt: 1,
                width: "95%",
                margin: "auto",
                minHeight: { xs: 170, sm: 190, md: 210 },
                display: "flex",
                alignItems: "center",
                backgroundColor: "black",
                backgroundImage: `
                  linear-gradient(
                    90deg,
                    rgba(8, 12, 18, 0.80) 0%,
                    rgba(8, 12, 18, 0.72) 40%,
                    rgba(8, 12, 18, 0.50) 72%,
                    rgba(8, 12, 18, 0.34) 100%
                  ),
                  url('/assets/HeroBanner1.png')
                `,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.42) 100%)",
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  px: { xs: 2, md: 4 },
                  py: { xs: 2.2, md: 2.5 },
                }}
              >
                <Stack spacing={1.2} alignItems="center" textAlign="center">
                  <Chip
                    icon={
                      <HourglassTopRoundedIcon sx={{ color: "white !important" }} />
                    }
                    label="Payment Processing"
                    sx={{
                      color: style.color4,
                      backgroundColor: style.accentGreen || style.color2,
                      fontWeight: 700,
                      borderRadius: "5px",
                      height: 30,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "white",
                      fontSize: { xs: "1.65rem", md: "2.2rem" },
                      fontWeight: 800,
                      lineHeight: 1.08,
                      letterSpacing: "-0.03em",
                      maxWidth: 700,
                    }}
                  >
                    Your payment is being processed.
                  </Typography>

                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "0.98rem", md: "1.05rem" },
                      lineHeight: 1.6,
                      maxWidth: 640,
                    }}
                  >
                    Thanks for your order. Some payment methods take a little longer
                    to confirm, but we’ll take care of the rest as soon as everything finishes processing.
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.25 },
                    borderRadius: "14px",
                    backgroundColor: panelBg,
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          color: style.color4,
                          fontSize: { xs: "1.12rem", md: "1.28rem" },
                          fontWeight: 800,
                        }}
                      >
                        We’re waiting for final confirmation
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        color: style.color3,
                        fontSize: "0.95rem",
                        lineHeight: 1.7,
                        maxWidth: 760,
                      }}
                    >
                      You do not need to submit payment again. If your payment is
                      approved, your purchase will be added to your account. If you
                      return later, you can check <a href='/my-gear'><strong>My Gear</strong></a> to see if
                      your download is ready.
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                        gap: 1.25,
                      }}
                    >
                      {infoItems.map((item) => (
                        <Stack
                          key={item.title}
                          spacing={1}
                          sx={{
                            p: 1.35,
                            borderRadius: "5px",
                            backgroundColor: panelBg2,
                            border: "1px solid rgba(255,255,255,0.06)",
                            minHeight: 132,
                          }}
                        >
                          {item.icon}
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 700,
                              lineHeight: 1.3,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: style.color3,
                              fontSize: "0.88rem",
                              lineHeight: 1.55,
                            }}
                          >
                            {item.text}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      sx={{ pt: 0.5 }}
                    >
                      <Button
                        component={RouterLink}
                        to="/my-gear"
                        startIcon={<DownloadRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 800,
                          px: 2.2,
                          py: 1,
                          color: style.color4,
                          backgroundColor: style.accentGreen || style.color2,
                          "&:hover": {
                            backgroundColor: style.accentGreen || style.color2,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        Go to My Gear
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/"
                        startIcon={<HomeRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2.2,
                          py: 1,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.09)",
                          },
                        }}
                      >
                        Return Home
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}